"use strict";
// Basic performance metrics suite.
Object.defineProperty(exports, "__esModule", { value: true });
exports.globals = void 0;
exports.newMetrics = newMetrics;
exports.measure = measure;
exports.updateMeasure = updateMeasure;
exports.childMetrics = childMetrics;
exports.childMetricsSingle = childMetricsSingle;
exports.metricsClean = metricsClean;
exports.metricsAggregate = metricsAggregate;
exports.metricsToString = metricsToString;
exports.metricsToJson = metricsToJson;
exports.metricsToRows = metricsToRows;
const _1 = require(".");
/**
 * @public
 */
exports.globals = newMetrics();
/**
 * @public
 * @returns
 */
function newMetrics() {
    return {
        operations: 0,
        value: 0,
        measurements: {},
        params: {},
        namedParams: {}
    };
}
function getUpdatedTopResult(current, time, params) {
    if (time === 0) {
        return current;
    }
    const result = current ?? [];
    const newValue = {
        value: time,
        params
    };
    if (result.length > 6) {
        if (result[0].value < newValue.value) {
            result[0] = newValue;
            return result;
        }
        if (result[result.length - 1].value > newValue.value) {
            result[result.length - 1] = newValue;
            return result;
        }
        // Shift the middle
        return [result[0], newValue, ...result.slice(1, 3), result[5]];
    }
    else {
        result.push(newValue);
        return result;
    }
}
/**
 * Measure with tree expansion. Operation counter will be added only to leaf's.
 * @public
 */
function measure(metrics, params, fullParams = {}, endOp) {
    const st = (0, _1.platformNow)();
    return () => {
        updateMeasure(metrics, st, params, fullParams, endOp);
    };
}
function updateMeasure(metrics, st, params, fullParams, endOp, value, override) {
    const ed = (0, _1.platformNow)();
    const fParams = typeof fullParams === 'function' ? fullParams() : fullParams;
    // Update params if required. Fast path: skip Object.entries alloc when empty
    // (the common case from middleware ctx.with('name', {}, op)).
    let firstKey;
    let extraCount = 0;
    for (const fk in params) {
        if (!Object.prototype.hasOwnProperty.call(params, fk))
            continue;
        if (firstKey === undefined) {
            firstKey = fk;
        }
        else {
            extraCount++;
        }
    }
    if (firstKey !== undefined) {
        const k = firstKey;
        const v = params[k];
        let bucket = metrics.params[k];
        if (bucket === undefined) {
            bucket = {};
            metrics.params[k] = bucket;
        }
        const vKey = `${v?.toString() ?? ''}`;
        let param = bucket[vKey];
        if (param === undefined) {
            param = { operations: 0, value: 0 };
            bucket[vKey] = param;
        }
        if (override === true) {
            if (value === 0) {
                // We need to delete value, to preserve sending zero values.
                // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                delete bucket[vKey];
            }
            else {
                param.operations = value ?? ed - st;
            }
        }
        else {
            param.value += value ?? ed - st;
            param.operations++;
        }
        // Update top results for params after the first one. Walk params again
        // without Object.entries alloc; skip the first key we already used.
        if (extraCount > 0) {
            param.topResult ??= [];
            const top = param.topResult;
            const dt = value ?? ed - st;
            for (const fk in params) {
                if (fk === k || !Object.prototype.hasOwnProperty.call(params, fk))
                    continue;
                const ev = params[fk];
                const evKey = `${ev}`;
                const r = top.find((it) => it.params[evKey] === true);
                if (r !== undefined) {
                    r.value += 1;
                    r.time = (r.time ?? 0) + dt;
                }
                else {
                    top.push({ params: { [evKey]: true }, value: 1, time: dt });
                }
            }
            top.sort((a, b) => b.value - a.value);
        }
    }
    // Update leaf data
    if (override === true) {
        metrics.operations = value ?? ed - st;
    }
    else {
        metrics.value += value ?? ed - st;
        metrics.operations++;
    }
    metrics.topResult = getUpdatedTopResult(metrics.topResult, ed - st, fParams);
    endOp?.(ed - st);
}
/**
 * @public
 */
function childMetrics(root, path) {
    // Single-segment fast path - hot from MeasureMetricsContext.newChild which
    // always passes a one-element array. Skips the for-of/segments alias overhead.
    if (path.length === 1) {
        const p = path[0];
        let v = root.measurements[p];
        if (v === undefined) {
            v = { operations: 0, value: 0, measurements: {}, params: {}, namedParams: {} };
            root.measurements[p] = v;
        }
        return v;
    }
    let oop = root;
    for (const p of path) {
        let v = oop.measurements[p];
        if (v === undefined) {
            v = { operations: 0, value: 0, measurements: {}, params: {}, namedParams: {} };
            oop.measurements[p] = v;
        }
        oop = v;
    }
    return oop;
}
/**
 * Same as childMetrics for a single name, but avoids the caller having to
 * allocate a temporary `[name]` array. Used by MeasureMetricsContext.newChild.
 * @public
 */
function childMetricsSingle(root, name) {
    let v = root.measurements[name];
    if (v === undefined) {
        v = { operations: 0, value: 0, measurements: {}, params: {}, namedParams: {} };
        root.measurements[name] = v;
    }
    return v;
}
function metricsClean(m) {
    // clean metrics from measure values.
    return {
        ...m,
        measurements: metricsCleanMeasurements(m.measurements)
    };
}
function metricsCleanMeasurements(m) {
    const result = {};
    for (const [k, v] of Object.entries(m)) {
        if (!k.startsWith('#')) {
            result[k] = metricsClean(v);
        }
    }
    return result;
}
/**
 * @public
 */
function metricsAggregate(m, limit = -1, roundMath = false) {
    let ms = aggregateMetrics(m.measurements, limit);
    // Use child overage, if there is no top level value specified.
    const me = Object.entries(ms);
    const sumVal = (me.length === 0 ? m.value : 0) +
        me
            .filter((it) => !it[0].startsWith('#'))
            .map((it) => it[1])
            .reduce((p, v) => {
            return p + v.value;
        }, 0);
    if (limit !== -1) {
        // We need to keep only top limit items in ms
        if (Object.keys(ms).length > 0) {
            const newMs = {};
            let added = 0;
            for (const [k, v] of Object.entries(ms)) {
                newMs[k] = v;
                added++;
                if (added >= limit) {
                    break;
                }
            }
            ms = newMs;
        }
    }
    return {
        operations: m.operations,
        measurements: ms,
        params: m.params,
        value: sumVal,
        topResult: m.topResult,
        namedParams: m.namedParams,
        opLog: m.opLog
    };
}
function aggregateMetrics(m, limit = -1) {
    const result = {};
    for (const [k, v] of Object.entries(m).sort((a, b) => b[1].value - a[1].value)) {
        result[k] = metricsAggregate(v, limit);
    }
    return result;
}
function toLen(val, sep, len) {
    while (val.length < len) {
        val += sep;
    }
    return val;
}
function printMetricsChildren(params, offset, length) {
    let r = '';
    if (Object.keys(params).length > 0) {
        r += '\n' + toLen('', ' ', offset);
        r += Object.entries(params)
            .filter((it) => it[1].value > 0.1)
            .map(([k, vv]) => toString(k, vv, offset, length))
            .join('\n' + toLen('', ' ', offset));
    }
    return r;
}
function printMetricsParams(params, offset, length) {
    let r = '';
    const joinP = (key, data) => {
        return Object.entries(data)
            .filter((it) => it[1].value >= 0.1)
            .map(([k, vv]) => `${toLen('', ' ', offset)}${toLen(key + '=' + k, '-', length - offset)}: avg ${Math.round((vv.value / (vv.operations > 0 ? vv.operations : 1)) * 100) / 100} total: ${Math.round(vv.value * 100) / 100} ops: ${vv.operations}`.trim());
    };
    const joinParams = Object.entries(params).reduce((p, c) => [...p, ...joinP(c[0], c[1])], []);
    if (Object.keys(joinParams).length > 0) {
        r += '\n' + toLen('', ' ', offset);
        r += joinParams.join('\n' + toLen('', ' ', offset));
    }
    return r;
}
function toString(name, m, offset, length) {
    let r = `${toLen('', ' ', offset)}${toLen(name, '-', length - offset)}: avg ${Math.round((m.value / (m.operations > 0 ? m.operations : 1)) * 100) / 100} total: ${Math.round(m.value * 100) / 100} ops: ${m.operations}`.trim();
    r += printMetricsParams(m.params, offset + 4, length);
    r += printMetricsChildren(m.measurements, offset + 4, length);
    return r;
}
function toJson(m) {
    const obj = {
        $total: m.value,
        $ops: m.operations
    };
    if (m.operations > 1) {
        obj.avg = Math.round((m.value / (m.operations > 0 ? m.operations : 1)) * 100) / 100;
    }
    if (Object.keys(m.params).length > 0) {
        obj.params = m.params;
    }
    for (const [k, v] of Object.entries(m.measurements ?? {})) {
        obj[`${k} ${v.value} ${v.operations} ${v.operations > 1 ? Math.round((v.value / (v.operations > 0 ? v.operations : 1)) * 100) / 100 : ''}`] = toJson(v);
    }
    return obj;
}
/**
 * @public
 */
function metricsToString(metrics, name = 'System', length) {
    return toString(name, metricsAggregate(metrics, 50, true), 0, length);
}
function metricsToJson(metrics) {
    return toJson(metricsAggregate(metrics));
}
function printMetricsParamsRows(params, offset) {
    const r = [];
    function joinP(key, data) {
        return Object.entries(data).map(([k, vv]) => [
            offset,
            `${key}=${k}`,
            Math.round((vv.value / (vv.operations > 0 ? vv.operations : 1)) * 100) / 100,
            Math.round(vv.value * 100) / 100,
            vv.operations
        ]);
    }
    for (const [k, v] of Object.entries(params)) {
        r.push(...joinP(k, v));
    }
    return r;
}
function printMetricsChildrenRows(params, offset) {
    const r = [];
    if (Object.keys(params).length > 0) {
        Object.entries(params).forEach(([k, vv]) => r.push(...toStringRows(k, vv, offset)));
    }
    return r;
}
function toStringRows(name, m, offset) {
    const r = [
        [
            offset,
            name,
            Math.round((m.value / (m.operations > 0 ? m.operations : 1)) * 100) / 100,
            Math.round(m.value * 100) / 100,
            m.operations
        ]
    ];
    r.push(...printMetricsParamsRows(m.params, offset + 1));
    r.push(...printMetricsChildrenRows(m.measurements, offset + 1));
    return r;
}
/**
 * @public
 */
function metricsToRows(metrics, name = 'System') {
    return toStringRows(name, metricsAggregate(metrics, 50, true), 0);
}
//# sourceMappingURL=metrics.js.map